// Olika typer av kontroller som kan läggas till i en rapportmall:

interface IRapportMallControlInfo {
  type: string;
  title: string;
  description: string;
  initialProps?: any;
}
export const RAPPORTMALL_TYPES: IRapportMallControlInfo[] = [
  {
    type: 'Text',
    title: 'Text',
    description: 'En text',
  },
  {
    type: 'TextBox',
    title: 'Textruta',
    description: 'En ram med en rubrik och en text',
  },
  {
    type: 'EditBox',
    title: 'Editerbar textruta',
    description: 'Ett fält som blir redigerbart i PDF-filen',
  },
  {
    type: 'CheckBox',
    title: 'Editerbar kryssruta',
    description: 'En kryssruta som blir ikryssbar i PDF-filen',
  },
  {
    type: 'Image',
    title: 'Bild',
    description: 'En bild',
  },
  {
    type: 'Box',
    title: 'Ruta',
    description: 'En ruta med ram',
  },
  {
    type: 'Line',
    title: 'Linje',
    description: 'En linje',
  },
  {
    type: 'Container',
    title: 'Grupp',
    description:
      'Osynlig layoutkomponent som används för att positionera andra komponenter inuti sig',
    initialProps: { Content: [] },
  },
  {
    type: 'Condition',
    title: 'Villkorad grupp',
    description:
      'Som en grupp (se ovan), men med ett villkor om den ska visas eller inte',
    initialProps: { Content: [] },
  },
  {
    type: 'DataSet',
    title: 'Tabell',
    description: 'En tabell med rubriker och rader från en undertabell',
  },
  {
    type: 'CustomGrid',
    title: 'Tabell med textrutor',
    description:
      'En layoutkomponent som skapar en tabell där varje ruta har rubrik och värde från fält i en undertabell. Tabellen upprepas för varje rad i undertabellen.',
    initialProps: { Height: undefined, Fields: [] },
  },
  {
    type: 'Association',
    title: 'Lista',
    description:
      'En layoutkomponent som skapar en grupp av komponenter för varje rad i en undertabell',
    initialProps: { Template: [] },
  },
  {
    type: 'Spacing',
    title: 'Mellanrum',
    description: 'Ett mellanrum i höjdled (flödande)',
  },
  {
    type: 'NewPage',
    title: 'Ny sida',
    description: 'En sidbrytning',
  },
  {
    type: 'AddressWindow',
    title: 'Adressfönster',
    description: 'Fönstret på ett fönsterkuvert',
  },
];

// Typer för RapportMall och dess kontroller

export interface IRapportMallRect {
  Width?: number;
  Height?: number;
  Left?: number;
  Top?: number;
  Right?: number;
  Bottom?: number;
}

export interface IRapportMallControl extends IRapportMallRect {
  Type: string;
}

export interface IRapportTextControl extends IRapportMallControl {
  Type: 'Text';
  Text: string;
  Multiline?: boolean;
  Formatted?: boolean;
  Size?: number;
  Style?: string;
  Align?: 'Left' | 'Center' | 'Right';
  VAlign?: 'Top' | 'Center' | 'Bottom';
  TextAlign?: 'Left' | 'Center' | 'Right';
}

export interface IRapportTextBoxControl extends IRapportMallControl {
  Type: 'TextBox';
  Caption: string;
  Text?: string;
}

export interface IRapportEditBoxControl extends IRapportMallControl {
  Type: 'EditBox';
  Name: string;
  Text?: string;
  Multiline?: boolean;
  Size?: number;
}

export interface IRapportCheckBoxControl extends IRapportMallControl {
  Type: 'CheckBox' | 'Checkbox';
  Name: string;
  Caption?: string;
  Checked?: boolean | string;
}

export interface IRapportImageControl extends IRapportMallControl {
  Type: 'Image';
  Field?: string;
}

export interface IRapportBoxControl extends IRapportMallControl {
  Type: 'Box';
}

export interface IRapportLineControl extends IRapportMallControl {
  Type: 'Line';
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
}

/** Base interface for controls with content */
export interface IRapportContentControl extends IRapportMallControl {
  Content: IRapportMallControl[];
}

export interface IRapportContainerControl extends IRapportContentControl {
  Type: 'Container';
  DefaultFont?: string;
}

export interface IRapportDataSetControl extends IRapportMallControl {
  Type: 'DataSet';
  Association?: string;
  Caption?: string;
  RowHeight?: number;
  ExtBorder?: boolean;
  Border?: boolean;
  Header?: boolean;
  MaxRowCount?: number;
  MinRowCount?: number;
  EmptyRowCount?: number;
  Fields?: Array<{ Name: string; Caption: string; Width?: number }>;
}
export interface IRapportCustomGridControl extends IRapportMallControl {
  Type: 'CustomGrid';
  Association?: string;
  MaxRowCount?: number;
  MinRowCount?: number;
  EmptyRowCount?: number;
  Fields?: Array<{
    Name: string;
    Caption: string;
    Width?: number;
    NewRow?: boolean;
    EditBox?: boolean;
  }>;
}
export interface IRapportAssociationControl extends IRapportMallControl {
  Type: 'Association';
  Association?: string;
  MaxRowCount?: number;
  MinRowCount?: number;
  EmptyRowCount?: number;
  Template: IRapportMallControl[];
}

export interface IRapportSpacingControl extends IRapportMallControl {
  Type: 'Spacing';
  Spacing?: number;
}

export interface IRapportNewPageControl extends IRapportMallControl {
  Type: 'NewPage';
}

export interface IRapportAddressWindowControl extends IRapportMallControl {
  Type: 'AddressWindow';
  Address?: string;
  Editable?: boolean;
}

export interface IRapportConditionControl extends IRapportContentControl {
  Type: 'Condition';
  Condition: string;
  Value: string;
  PreviewCondition: string;
  PreviewValue: string;
  DefaultFont?: string;
}

// Type guards
export function isRapportTextBoxControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportTextBoxControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'textbox'
  );
}
export function isRapportEditBoxControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportEditBoxControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'editbox'
  );
}
export function isRapportCheckBoxControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportCheckBoxControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'checkbox'
  );
}
export function isRapportImageControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportImageControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'image'
  );
}
export function isRapportBoxControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportBoxControl {
  return (
    !!ctrl && typeof ctrl.Type === 'string' && ctrl.Type.toLowerCase() === 'box'
  );
}
export function isRapportLineControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportLineControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'line'
  );
}

export function isRapportContentControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportContentControl {
  return !!ctrl && Array.isArray((ctrl as IRapportContentControl).Content);
}

export function isRapportContainerControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportContainerControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'container'
  );
}
export function isRapportDataSetControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportDataSetControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'dataset'
  );
}
export function isRapportCustomGridControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportCustomGridControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'customgrid'
  );
}
export function isRapportAssociationControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportAssociationControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'association'
  );
}
export function isRapportSpacingControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportSpacingControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'spacing'
  );
}
export function isRapportNewPageControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportNewPageControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'newpage'
  );
}
export function isRapportAddressWindowControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportAddressWindowControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'addresswindow'
  );
}
export function isRapportTextControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportTextControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'text'
  );
}
export function isRapportConditionControl(
  ctrl: IRapportMallControl | null
): ctrl is IRapportConditionControl {
  return (
    !!ctrl &&
    typeof ctrl.Type === 'string' &&
    ctrl.Type.toLowerCase() === 'condition'
  );
}

export function mallControlInfo(
  ctrl: IRapportMallControl | null | undefined
): IRapportMallControlInfo | undefined {
  if (ctrl && typeof ctrl.Type === 'string') {
    const type = ctrl.Type;
    const info = RAPPORTMALL_TYPES.find(
      t => t.type.toLowerCase() === type.toLowerCase()
    );
    return info;
  }
  return undefined;
}
